export interface NativeAuthPayload {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  expiresAtEpochSec?: number;
  user?: {
    id?: string;
    email?: string;
    name?: string;
    picture?: string;
  };
}

export interface GatewayToken {
  toString: () => string;
}

export interface GatewaySession {
  tokens?: {
    idToken?: GatewayToken;
    accessToken?: GatewayToken;
    refreshToken?: GatewayToken;
  };
}

export interface GatewayUser {
  userId: string;
  username: string;
  signInDetails?: {
    loginId?: string;
  };
}

interface AndroidAuthBridge {
  isNativeAuthEnabled?: () => boolean;
  getAuthSessionJson?: () => string;
  requestSignIn?: () => void;
  signOut?: () => void;
}

declare global {
  interface Window {
    AndroidAuth?: AndroidAuthBridge;
  }
}

const NATIVE_AUTH_STORAGE_KEY = 'thinklet.nativeAuthSession';

const decodeJwtPayload = (token: string): Record<string, any> | null => {
  try {
    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }
    const base64Url = parts[1];
    if (!base64Url) {
      return null;
    }

    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4 !== 0) {
      base64 += '=';
    }

    const decoded = atob(base64);
    const jsonPayload = decodeURIComponent(
      decoded
        .split('')
        .map((char) => `%${(`00${char.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

const toToken = (value?: string): GatewayToken | undefined => {
  if (!value) {
    return undefined;
  }
  return {
    toString: () => value,
  };
};

const parseNativeAuthPayload = (raw?: string | null): NativeAuthPayload | null => {
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as NativeAuthPayload;
    if (!parsed.idToken || !parsed.accessToken) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const persistNativeAuthPayload = (raw?: string): void => {
  try {
    if (!raw) {
      window.localStorage.removeItem(NATIVE_AUTH_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(NATIVE_AUTH_STORAGE_KEY, raw);
  } catch {
    // localStorage is best-effort only.
  }
};

const loadPersistedNativeAuthPayload = (): NativeAuthPayload | null => {
  try {
    return parseNativeAuthPayload(window.localStorage.getItem(NATIVE_AUTH_STORAGE_KEY));
  } catch {
    return null;
  }
};

const safeBridgeCall = <T>(name: string, fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch (error) {
    console.error(`[NativeBridge:${name}]`, error);
    return fallback;
  }
};

export const isNativeAuthMode = (): boolean => {
  if (!window.AndroidAuth) {
    return false;
  }
  if (typeof window.AndroidAuth.isNativeAuthEnabled === 'function') {
    return safeBridgeCall(
      'isNativeAuthEnabled',
      () => window.AndroidAuth?.isNativeAuthEnabled?.() === true,
      false
    );
  }
  return true;
};

export const requestNativeSignIn = (): void => {
  safeBridgeCall(
    'requestSignIn',
    () => {
      window.AndroidAuth?.requestSignIn?.();
      return undefined;
    },
    undefined
  );
};

export const nativeSignOut = (): void => {
  safeBridgeCall(
    'signOut',
    () => {
      window.AndroidAuth?.signOut?.();
      return undefined;
    },
    undefined
  );
};

export const getNativeAuthPayload = (): NativeAuthPayload | null => {
  const bridgeRaw = safeBridgeCall(
    'getAuthSessionJson',
    () => window.AndroidAuth?.getAuthSessionJson?.(),
    undefined
  );
  const bridgePayload = parseNativeAuthPayload(bridgeRaw);
  if (bridgePayload) {
    persistNativeAuthPayload(bridgeRaw);
    return bridgePayload;
  }

  return loadPersistedNativeAuthPayload();
};

export const getNativeAuthSession = (): GatewaySession | null => {
  const payload = getNativeAuthPayload();
  if (!payload) {
    return null;
  }

  return {
    tokens: {
      idToken: toToken(payload.idToken),
      accessToken: toToken(payload.accessToken),
      refreshToken: toToken(payload.refreshToken),
    },
  };
};

export const getNativeCurrentUser = (): GatewayUser | null => {
  const payload = getNativeAuthPayload();
  if (!payload) {
    return null;
  }

  const jwt = decodeJwtPayload(payload.idToken);
  const cognitoUsername = (jwt?.['cognito:username'] || jwt?.sub || payload.user?.id || '').toString();
  if (!cognitoUsername) {
    return null;
  }

  const userId = cognitoUsername.startsWith('Google_')
    ? cognitoUsername.substring(7)
    : cognitoUsername;

  return {
    userId,
    username: payload.user?.name || (jwt?.name as string) || userId,
    signInDetails: {
      loginId: payload.user?.email || (jwt?.email as string) || undefined,
    },
  };
};

export const getNativeUserIdHint = (): string | undefined => {
  return getNativeCurrentUser()?.userId;
};
